import { createTheme } from '@mui/material/styles';

// Light Theme
export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#00A16A', // Emerald Green
      light: '#4DB887',
      dark: '#007A51',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#1976D2', // Dodger Blue
      light: '#42A5F5',
      dark: '#1565C0',
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#D32F2F', // Crimson
      light: '#EF5350',
      dark: '#C62828',
      contrastText: '#FFFFFF',
    },
    warning: {
      main: '#FFA000', // Amber
      light: '#FFB74D',
      dark: '#F57C00',
      contrastText: '#000000',
    },
    info: {
      main: '#1976D2', // Dodger Blue
      light: '#42A5F5',
      dark: '#1565C0',
      contrastText: '#FFFFFF',
    },
    success: {
      main: '#00A16A', // Emerald Green
      light: '#4DB887',
      dark: '#007A51',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#F7F8FA', // Soft Pearl
      paper: '#EDEFF2', // Mist Gray
    },
    text: {
      primary: '#1E1E1E', // Dark Charcoal
      secondary: '#5F6368', // Medium Gray
      disabled: '#9E9E9E',
    },
    divider: '#D1D5DB', // Light Slate
    grey: {
      50: '#F7F8FA',
      100: '#EDEFF2',
      200: '#D1D5DB',
      300: '#B0B7C3',
      400: '#8B95A1',
      500: '#5F6368',
      600: '#4A5568',
      700: '#2D3748',
      800: '#1A202C',
      900: '#1E1E1E',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 600,
      color: '#1E1E1E',
    },
    h2: {
      fontWeight: 600,
      color: '#1E1E1E',
    },
    h3: {
      fontWeight: 600,
      color: '#1E1E1E',
    },
    h4: {
      fontWeight: 600,
      color: '#1E1E1E',
    },
    h5: {
      fontWeight: 500,
      color: '#1E1E1E',
    },
    h6: {
      fontWeight: 500,
      color: '#1E1E1E',
    },
    body1: {
      color: '#1E1E1E',
    },
    body2: {
      color: '#5F6368',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#F7F8FA',
          color: '#1E1E1E',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#EDEFF2',
          color: '#1E1E1E',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#EDEFF2',
          borderRight: '1px solid #D1D5DB',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#EDEFF2',
          border: '1px solid #D1D5DB',
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          backgroundColor: '#EDEFF2',
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#F7F8FA',
            borderBottom: '2px solid #D1D5DB',
          },
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid #D1D5DB',
          },
          '& .MuiDataGrid-footerContainer': {
            backgroundColor: '#F7F8FA',
            borderTop: '1px solid #D1D5DB',
          },
        },
      },
    },
  },
});

// Dark Theme (current theme - updated with original colors)
export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#00B97D', // Emerald Green
      light: '#4DB887',
      dark: '#007A51',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#2196F3', // Dodger Blue
      light: '#42A5F5',
      dark: '#1565C0',
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#F44336', // Crimson
      light: '#e57373',
      dark: '#d32f2f',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#FFC107', // Amber
      light: '#ffb74d',
      dark: '#f57c00',
      contrastText: '#000000',
    },
    info: {
      main: '#2196F3', // Dodger Blue
      light: '#64b5f6',
      dark: '#1976d2',
      contrastText: '#ffffff',
    },
    success: {
      main: '#00B97D', // Emerald Green
      light: '#4DB887',
      dark: '#007A51',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#1C1F26', // Gunmetal
      paper: '#262A33', // Charcoal
    },
    text: {
      primary: '#FFFFFF', // White
      secondary: '#B0B3B8', // Light Gray
      disabled: 'rgba(255, 255, 255, 0.5)',
    },
    divider: '#32363F', // Dark Slate
    grey: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#eeeeee',
      300: '#e0e0e0',
      400: '#bdbdbd',
      500: '#9e9e9e',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 600,
      color: '#FFFFFF',
    },
    h2: {
      fontWeight: 600,
      color: '#FFFFFF',
    },
    h3: {
      fontWeight: 600,
      color: '#FFFFFF',
    },
    h4: {
      fontWeight: 600,
      color: '#FFFFFF',
    },
    h5: {
      fontWeight: 600,
      color: '#FFFFFF',
    },
    h6: {
      fontWeight: 600,
      color: '#FFFFFF',
    },
    body1: {
      color: '#FFFFFF',
    },
    body2: {
      color: '#B0B3B8',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#1C1F26',
          color: '#FFFFFF',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#262A33',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#262A33',
          borderRight: '1px solid #32363F',
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          backgroundColor: '#262A33',
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#1C1F26',
            borderBottom: '2px solid #32363F',
          },
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid #32363F',
          },
          '& .MuiDataGrid-footerContainer': {
            backgroundColor: '#1C1F26',
            borderTop: '1px solid #32363F',
          },
        },
      },
    },
  },
});

export const getTheme = (mode = 'dark') => {
  return mode === 'light' ? lightTheme : darkTheme;
};
